/**
 * T144: API Error Handler with user-friendly messages
 * Centralizes error handling for all API failures
 */

import { AxiosError } from 'axios';

export interface ApiError {
  status: number;
  message: string;
  details?: Record<string, any>;
  timestamp?: string;
}

/**
 * User-friendly error messages for common scenarios
 */
const errorMessageMap: Record<number, string> = {
  400: 'Dados inválidos. Verifique os campos e tente novamente.',
  401: 'Sessão expirada. Faça login novamente.',
  403: 'Você não tem permissão para acessar este recurso.',
  404: 'Recurso não encontrado.',
  409: 'Ação não permitida. Este recurso já foi utilizado.',
  422: 'Validação falhou. Verifique os dados enviados.',
  429: 'Muitas requisições. Aguarde um momento e tente novamente.',
  500: 'Erro no servidor. Tente novamente mais tarde.',
  502: 'Serviço temporariamente indisponível. Tente novamente.',
  503: 'Serviço em manutenção. Tente novamente mais tarde.',
};

/**
 * Specific error messages for profile-related errors
 */
const profileErrorMessageMap: Record<string, string> = {
  'Perfil não encontrado': 'Você precisa completar seu perfil antes de responder pesquisas. Acesse sua área de perfil para preencher as informações necessárias.',
  'Seu perfil está desatualizado': 'Seu perfil está desatualizado. Acesse sua área de perfil para atualizar suas informações pessoais e depois tente novamente.',
  'Profile not found': 'Você precisa completar seu perfil antes de responder pesquisas. Acesse sua área de perfil para preencher as informações necessárias.',
};

/**
 * Generic error labels that should be replaced with specific messages
 * These are broad error types that don't provide useful context
 */
const genericErrorLabels = ['Validation Error', 'Bad Request', 'Request failed'];

/**
 * Parse Axios error and return user-friendly message
 */
export function parseApiError(error: unknown): ApiError {
  if (error instanceof AxiosError) {
    const status = error.response?.status || 500;
    const errorData = error.response?.data as any;

    // Priority order for message extraction:
    // 1. Specific error message from backend (message field)
    // 2. Generic error type from backend (error field) - only if specific message exists
    // 3. If no specific message, use status-based fallback
    let message = '';
    const hasSpecificMessage = errorData?.message && typeof errorData.message === 'string' && errorData.message.trim();
    const hasGenericError = errorData?.error && typeof errorData.error === 'string' && errorData.error.trim();

    // First, check for a specific message from backend
    if (hasSpecificMessage) {
      message = errorData.message.trim();
    }
    // Only fall back to error field if it's NOT a generic label
    else if (hasGenericError && !genericErrorLabels.includes(errorData.error)) {
      message = errorData.error.trim();
    }
    // Only use generic map as last resort
    else {
      message = errorMessageMap[status] || 'Erro desconhecido';
    }

    // Check if this is a profile-related error and enhance the message
    for (const [profileErrorKey, profileErrorMsg] of Object.entries(profileErrorMessageMap)) {
      if (message.includes(profileErrorKey)) {
        message = profileErrorMsg;
        break;
      }
    }

    // For validation errors, include field details if available
    const details = errorData?.details || errorData?.errors;

    return {
      status,
      message,
      details,
      timestamp: new Date().toISOString(),
    };
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    return {
      status: 500,
      message: error.message || 'Erro desconhecido',
      timestamp: new Date().toISOString(),
    };
  }

  // Fallback
  return {
    status: 500,
    message: 'Erro desconhecido',
    timestamp: new Date().toISOString(),
  };
}

/**
 * Get user-friendly error message
 */
export function getUserFriendlyMessage(error: unknown): string {
  const apiError = parseApiError(error);
  return apiError.message;
}

/**
 * Check if error is due to authentication issue
 */
export function isAuthError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return error.response?.status === 401 || error.response?.status === 403;
  }
  return false;
}

/**
 * Check if error is a validation error
 */
export function isValidationError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return error.response?.status === 400 || error.response?.status === 422;
  }
  return false;
}

/**
 * Check if error is a network/timeout error
 */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof AxiosError) {
    return !error.response; // No response means network error
  }
  return false;
}

/**
 * Get detailed field validation errors
 */
export function getValidationErrors(error: unknown): Record<string, string> {
  const apiError = parseApiError(error);
  const errors: Record<string, string> = {};

  if (apiError.details) {
    if (Array.isArray(apiError.details)) {
      // Handle array of validation errors
      for (const err of apiError.details) {
        if (err.field && err.message) {
          errors[err.field] = err.message;
        }
      }
    } else if (typeof apiError.details === 'object') {
      // Handle object of field errors
      for (const [field, message] of Object.entries(apiError.details)) {
        errors[field] = String(message);
      }
    }
  }

  return errors;
}

export default {
  parseApiError,
  getUserFriendlyMessage,
  isAuthError,
  isValidationError,
  isNetworkError,
  getValidationErrors,
};
